import * as Sentry from "@sentry/react-native";
import { t } from "@transifex/native";
import { useFocusEffect, usePathname, useRouter } from "expo-router";
import { useCallback, useMemo, useState } from "react";

import { useContactValidation } from "@/hooks/useContactValidation";
import { useSelectPlate } from "@/hooks/useSelectPlate";
import { fetchPlate } from "@/services/plate.queries";
import { validateLicensePlate } from "@/utils/licensePlateValidation";
import { getValueFromStorage, pushToStorageArray, saveToStorage } from "@/utils/secureStore";

import ArrowLeft from "@/assets/icons/ArrowLeft";
import ArrowRight from "@/assets/icons/ArrowRight";
import Screen from "@/components/layout/Screen";
import AppButton from "@/components/shared/AppButton";
import ContactInput from "@/components/shared/LicensePlatePicker/ContactInput";
import { LicensePlateInput } from "@/components/shared/LicensePlatePicker/LicensePlateInput";
import { LicensePlateRadio } from "@/components/shared/LicensePlatePicker/LicensePlateRadio";
import PageTitle from "@/components/shared/PageTitle/PageTitle";
import PlateModal from "@/components/shared/PlateModal";
import PlateWithWarningModal from "@/components/shared/PlateWithWarningModal";
import SessionInProgressModal from "@/components/shared/SessionInProgressModal";
import TermsTextLink from "@/components/shared/TermsTextLink";
import { Box } from "@/components/ui/box";
import { HStack } from "@/components/ui/hstack";
import { Skeleton, SkeletonText } from "@/components/ui/skeleton";
import { Text } from "@/components/ui/text";
import { VStack } from "@/components/ui/vstack";
import { useToastNotify } from "@/stores/toastStore";
import { isEmail, isPhone } from "@/utils/etc";
import { formatPhoneNumber, getPhoneDigits } from "@/utils/formatPhoneNumber";
import { T } from "@transifex/react";
import type { WarningData } from "ipctg-inflow-types/InFlowAPIMisc";
import { Platform } from "react-native";

export default function PurchaseSinglePass() {
  const router = useRouter();
  const pathname = usePathname();
  const {
    guestPlates,
    fetchGuestPlates,
    inputValue,
    setInputValue,
    setLicensePlate,
    selectedPlate,
    setSelectedPlate,
    isRemoveModalOpen,
    setIsRemoveModalOpen,
    plateToRemove,
    isRemoving,
    errors,
    setErrors,
    handleDeletePlate,
    confirmRemovePlate,
    handleLicensePlateChange,
    clearLicensePlateError,
    account,
    isAccountFetching,
    isAccountPending,
    refetchAccount,
  } = useSelectPlate();
  const [defaultSaved, setDefaultSaved] = useState<{ phone: string; email: string }>();
  const [contact, setContact] = useState<string>();
  const [isPhoneForm, setIsPhoneForm] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [plateInputError, setPlateInputError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isSessionInProgressModalOpen, setIsSessionInProgressModalOpen] = useState(false);
  const [alreadyPaidData, setAlreadyPaidData] = useState({
    receiptID: "",
    paidLotName: "",
    canExtendSession: false,
    sessionID: "",
  });
  const [isPlateWarningDataModalOpen, setIsPlateWarningDataModalOpen] = useState(false);
  const [plateWarningData, setPlateWarningData] = useState<WarningData | null>(null);

  const { error, errorMessage, validateContact, clearError } = useContactValidation();
  const { notify } = useToastNotify();

  const resetState = useCallback(() => {
    setInputValue("");
    setLicensePlate("");
    setSelectedPlate("");
    setIsPhoneForm(true);
    setPlateInputError(false);
    setContact(undefined);
    setErrors({ radio: null, licensePlate: null, contact: null });
  }, [setInputValue, setLicensePlate, setSelectedPlate, setErrors]);

  useFocusEffect(
    useCallback(() => {
      if (pathname === "/purchase/singlepass") {
        resetState();
        refetchAccount();
      }
    }, [pathname, resetState, refetchAccount]),
  );

  useFocusEffect(
    useCallback(() => {
      if (contact === undefined && !isAccountPending) {
        if (account?.phone) {
          setContact(account.phone);
          setIsPhoneForm(true);
        } else if (account?.email) {
          setContact(account.email);
          setIsPhoneForm(false);
        } else {
          getValueFromStorage("contact").then((storedContact) => {
            if (storedContact) {
              setDefaultSaved({ phone: isPhone(storedContact) ? storedContact : "", email: isEmail(storedContact) ? storedContact : "" });
              setContact(storedContact);
              setIsPhoneForm(isPhone(storedContact));
            }
          });
        }
      }

      if (!account) {
        fetchGuestPlates();
      }
    }, [account, contact, isAccountPending, fetchGuestPlates]),
  );

  const initialContactValue = useMemo(() => {
    if (!isAccountPending) {
      if (account) {
        return { phone: account.phone, email: account.email };
      }
      if (defaultSaved) {
        return defaultSaved;
      }
    }
    return { phone: formatPhoneNumber(""), email: "" };
  }, [account, defaultSaved, isAccountPending]);

  const handleSubmit = async () => {
    const newErrors: {
      radio: string | null;
      licensePlate: string | null;
      contact: string | null;
    } = {
      radio: null,
      licensePlate: null,
      contact: null,
    };

    if (account?.savedPlates && account.savedPlates.length > 0 && !selectedPlate) {
      newErrors.radio = t("Please select an option.");
    } else if (!account && guestPlates?.length && !selectedPlate) {
      newErrors.radio = t("Please select an option.");
    }

    const plateError = validateLicensePlate(inputValue);
    if (plateError) {
      newErrors.licensePlate = t(plateError);
      setPlateInputError(true);
    } else {
      setPlateInputError(false);
    }

    const contactValid = validateContact({ contact, isPhoneForm });

    if (!contactValid) {
      newErrors.contact = errorMessage;
    }

    setErrors(newErrors);

    if (Object.values(newErrors).every((error) => error === null)) {
      setIsLoading(true);
      try {
        if (contact) {
          await saveToStorage("contact", !isEmail(contact) ? `+${getPhoneDigits(contact)}` : contact);
        }
        const response = await fetchPlate({ plate: inputValue });
        if (response.alreadyPaid) {
          setAlreadyPaidData(response.alreadyPaidData);
          setIsSessionInProgressModalOpen(true);
        } else if (response.warning && response.warningData) {
          setIsPlateWarningDataModalOpen(true);
          setPlateWarningData(response.warningData);
        } else if (!response.plateInConvenience) {
          setIsModalOpen(true);
        } else {
          await saveToStorage("licensePlate", inputValue);
          router.push("/selectrate");
        }
      } catch (error) {
        console.error("Error fetching plate information:", error);
        Sentry.captureException(error, {
          extra: {
            data: { plate: inputValue },
            localStorage: Platform.OS === "web" ? window.localStorage : null,
          },
        });
        notify({ severity: "error", message: t("An error occurred while processing your request.") });
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleChangeContact = (newContact: string, newIsPhoneForm: boolean) => {
    if (newIsPhoneForm !== isPhoneForm) {
      setContact("");
    }
    setContact(newContact);
    setIsPhoneForm(newIsPhoneForm);
    clearError();
  };

  const handleSelectPlate = (value: string) => {
    const formattedPlate = value.trim().toLocaleUpperCase();
    setSelectedPlate(formattedPlate);
    if (formattedPlate !== "NEW") {
      setInputValue(formattedPlate);
      setLicensePlate(formattedPlate);
      setPlateInputError(false);
    } else {
      setInputValue("");
      setLicensePlate("");
      setPlateInputError(true);
    }
    setErrors((prevErrors) => ({ ...prevErrors, radio: null }));
  };

  if (isAccountFetching) {
    return <PurchaseSinglePassSkeleton hasSavedPlates={!!account?.savedPlates?.length} />;
  }

  const handleConfirm = async () => {
    try {
      if (!account) {
        await pushToStorageArray("guestRecentPlates", inputValue);
      }
      await saveToStorage("licensePlate", inputValue);
      if (contact) {
        await saveToStorage("contact", !isEmail(contact) ? `+${getPhoneDigits(contact)}` : contact);
      }
      setIsModalOpen(false);
      setIsPlateWarningDataModalOpen(false);
      router.push("/selectrate");
    } catch (error) {
      console.error("Error saving data:", error);
      Sentry.captureException(error, {
        extra: {
          localStorage: Platform.OS === "web" ? window.localStorage : null,
        },
      });
      notify({ severity: "error", message: t("An error occurred while processing your request.") });
    }
  };

  return (
    <Screen>
      <VStack className="gap-[32px]">
        <PageTitle title={t("Please provide your information")} />

        <VStack className="gap-[20px]">
          {account?.savedPlates && account.savedPlates.length > 0 ? (
            <>
              <LicensePlateRadio
                selectedValue={selectedPlate}
                onValueChange={handleSelectPlate}
                plates={account.savedPlates.map((plate) => ({
                  id: plate.plate,
                  plate: plate.plate,
                }))}
                onDelete={handleDeletePlate}
              />

              {errors.radio && (
                <Box>
                  <Text className="text-error-100 text-[14px] leading-[20px]">{errors.radio}</Text>
                </Box>
              )}
            </>
          ) : (
            // SHOW INPUT IF THERE ISNT SAVED PLATES
            <>
              {!account && guestPlates && guestPlates.length > 0 ? (
                <>
                  <LicensePlateRadio
                    selectedValue={selectedPlate}
                    onValueChange={handleSelectPlate}
                    plates={guestPlates.map((plate) => ({
                      id: plate,
                      plate: plate,
                    }))}
                    onDelete={handleDeletePlate}
                  />
                  {errors.radio && (
                    <Box>
                      <Text className="text-error-100 text-[14px] leading-[20px]">{errors.radio}</Text>
                    </Box>
                  )}
                </>
              ) : (
                <LicensePlateInput
                  onAdd={handleLicensePlateChange}
                  setInputValue={setInputValue}
                  inputValue={inputValue}
                  setInputError={setPlateInputError}
                  inputError={plateInputError}
                  errorMessage={errors.licensePlate}
                  clearError={clearLicensePlateError}
                />
              )}
            </>
          )}
          {/* SHOW INPUT IF "ENTER NEW PLATE" IS SELECTED */}
          {selectedPlate === "NEW" && (
            <LicensePlateInput
              onAdd={handleLicensePlateChange}
              setInputValue={setInputValue}
              inputValue={inputValue}
              setInputError={setPlateInputError}
              inputError={plateInputError}
              errorMessage={errors.licensePlate}
              clearError={clearLicensePlateError}
            />
          )}
        </VStack>
        <Box className="gap-[8px]">
          <ContactInput onChange={handleChangeContact} error={error} errorMessage={errorMessage} clearError={clearError} initialValue={initialContactValue} />
          <Text className="text-typography-900 text-[14px] font-[400] leading-[20px] text-center pt-4">
            <T _str="Your information will only be used for communications related to this service." />
          </Text>
          <TermsTextLink />
        </Box>
        <HStack className="gap-[12px]">
          <AppButton
            onPress={() => {
              resetState();
              if (!router.canGoBack()) {
                router.replace("/welcome");
              } else {
                router.back();
              }
            }}
            title={t("Back")}
            variant="outline"
            iconPosition="before"
            icon={ArrowLeft}
          />
          <AppButton
            className="flex-1"
            loading={isLoading}
            onPress={handleSubmit}
            title={t("Agree and Continue")}
            variant="primary"
            icon={ArrowRight}
            iconPosition="after"
          />
        </HStack>
      </VStack>

      <PlateModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} licensePlate={inputValue} onConfirm={handleConfirm} type={"confirm"} />

      <PlateModal
        isOpen={isRemoveModalOpen}
        type="remove"
        licensePlate={plateToRemove}
        onClose={() => setIsRemoveModalOpen(false)}
        onConfirm={confirmRemovePlate}
        isRemoving={isRemoving}
      />

      <SessionInProgressModal
        isOpen={isSessionInProgressModalOpen}
        onClose={() => setIsSessionInProgressModalOpen(false)}
        alreadyPaidData={alreadyPaidData}
        plate={inputValue}
      />

      <PlateWithWarningModal
        isOpen={isPlateWarningDataModalOpen}
        onClose={() => setIsPlateWarningDataModalOpen(false)}
        cameraLotName={plateWarningData?.cameraLotName ?? ""}
        qrLotName={plateWarningData?.qrLotName ?? ""}
        onConfirm={handleConfirm}
      />
    </Screen>
  );
}

const PurchaseSinglePassSkeleton = ({ hasSavedPlates = false }) => {
  return (
    <Screen>
      <VStack className="gap-[32px]">
        <Box className="flex justify-center items-center">
          <SkeletonText className="h-[20px] w-3/4" />
        </Box>

        <VStack className="gap-[20px]">
          {hasSavedPlates ? (
            <VStack className="gap-[12px]">
              <SkeletonText className="h-[14px] w-1/2" />
              {[1, 2].map((i) => (
                <HStack key={i} className="items-center gap-[12px]">
                  <Skeleton className="h-[52px] w-full rounded-md" />
                </HStack>
              ))}
            </VStack>
          ) : (
            <Box>
              <Skeleton className="h-[52px] w-full rounded-md" />
            </Box>
          )}
        </VStack>

        <VStack className="gap-[12px]">
          <SkeletonText className="h-[14px] w-1/2" />
          <Skeleton className="h-[52px] w-full rounded-md" />
        </VStack>
        <VStack className="gap-[8px] items-center">
          <SkeletonText _lines={2} className="h-[14px] w-[80%] self-center" />
          <SkeletonText className="h-[14px] w-3/4" />
          <SkeletonText className="h-[14px] w-1/2" />
        </VStack>

        <HStack className="gap-[12px]">
          <Skeleton className="h-[52px] w-1/3 rounded-md" />
          <Skeleton className="h-[52px] w-2/3 rounded-md" />
        </HStack>
      </VStack>
    </Screen>
  );
};
